import _ from 'underscore';
import Compressor from 'compressorjs';
import moment from 'moment';

// Same Vision key used by PMS (envConfig.googleAPIKey). Prefer env overrides when set.
const GoogleAPIKey =
  process.env.REACT_APP_API_URL === 'prod-build' ||
  process.env.REACT_APP_API_URL === 'promenade-build'
    ? process.env.REACT_APP_OCR_PRO_KEY ||
      process.env.REACT_APP_GOOGLE_API_KEY ||
      'AIzaSyDYu0jmtUpyzSOFRQ9R5xW_2g_djZtUh_E'
    : process.env.REACT_APP_OCR_KEY ||
      process.env.REACT_APP_GOOGLE_API_KEY ||
      'AIzaSyDYu0jmtUpyzSOFRQ9R5xW_2g_djZtUh_E';

const readAsDataURL = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('FileReader error'));
  });

// Compress the image and stamp the "HotelX use only" watermark, resolving with
// the resulting blob. Everything is scoped to this call so concurrent uploads
// (e.g. Front and Back) never share state.
const watermark = (file: File): Promise<Blob> =>
  new Promise((resolve, reject) => {
    // eslint-disable-next-line no-new
    new Compressor(file, {
      strict: false,
      drew(context: any, canvas: any) {
        const fontSize = canvas.height * 0.2;
        context.fillStyle = '#000';
        context.font = `${fontSize}px serif`;
        context.textAlign = 'center';
        context.fillText('HotelX use only', canvas.width / 2, canvas.height / 2);
      },
      success(result: any) {
        resolve(result);
      },
      error(err) {
        reject(err);
      },
    });
  });

const compressForOCR = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    // Higher quality helps Vision read fine MyKad text (apostrophes / mid-name lines)
    // eslint-disable-next-line no-new
    new Compressor(file, {
      quality: 0.95,
      maxWidth: 2500,
      maxHeight: 2500,
      success(result: any) {
        const reader = new FileReader();
        reader.readAsDataURL(result);
        reader.onload = () => {
          const dataUrl = reader.result as string;
          resolve(dataUrl.split('base64,')[1]);
        };
        reader.onerror = () => reject(new Error('FileReader error'));
      },
      error(err) {
        reject(err);
      },
    });
  });

const submitToGoogle = async (uri: string) => {
  try {
    const body = JSON.stringify({
      requests: [
        {
          features: [
            {
              type: 'DOCUMENT_TEXT_DETECTION',
              maxResults: 50,
            },
            {
              type: 'TEXT_DETECTION',
              maxResults: 50,
            },
          ],
          image: {
            content: uri,
          },
        },
      ],
    });

    const response = await fetch(
      `https://vision.googleapis.com/v1/images:annotate?key=${GoogleAPIKey}`,
      {
        headers: {
          Accept: 'application/json',
        },
        method: 'POST',
        body,
      }
    );
    const responseJson = await response.json();
    return await OCRProcessing(responseJson);
  } catch (error) {
    console.log(error);
    return null;
  }
};

const getPassportCountry = async (countryCode: string) => {
  try {
    const endpoint = 'https://countries.dev/alpha/';
    const response = await fetch(
      `${endpoint}${countryCode}?fields=name,demonym,alpha2Code,alpha3Code`,
      { method: 'GET' }
    );
    const result = await response.json();
    const country = result?.name || '';
    const nationality = result?.demonym || '';
    return { country, nationality };
  } catch (error) {
    return { country: '', nationality: '' };
  }
};

const parseMyKad = (
  lines: string[]
): {
  name?: string;
  nationality?: string;
  nric?: string;
  address?: string;
  country?: string;
  state?: string;
  city?: string;
  postcode?: string;
} => {
  const norm = (s: string) =>
    s
      .replace(/[''`′´]/g, "'") // normalize apostrophes (A'ZIM)
      .replace(/\s+/g, ' ')
      .trim()
      .toUpperCase();
  const uniqueLines = Array.from(new Set(lines.map(norm)));

  const headerNoise = new Set([
    'KAD PENGENALAN',
    'IDENTITY CARD',
    'MYKAD',
    'KERAJAAN MALAYSIA',
    'KERAJAAN',
    'MALAYSIA',
    'STARE',
    'AY STARE',
    'KERA',
  ]);

  const religions = new Set([
    'ISLAM',
    'BUDDHA',
    'BUDDHIST',
    'CHRISTIAN',
    'HINDU',
    'SIKH',
    'TAOIST',
    'CONFUCIAN',
    'OTHER',
    'LAIN-LAIN',
    'TIADA',
    'NONE',
  ]);

  const genders = new Set(['LELAKI', 'PEREMPUAN', 'MALE', 'FEMALE']);

  const isHeaderLike = (s: string) => {
    if (headerNoise.has(s)) return true;
    if (s.includes('KERAJAAN') || s.includes('RAJAAN')) return true;
    if (s.includes('PENGENAL')) return true; // KAD PENGENALAN / OCR truncations
    if (s.includes('IDENTITY')) return true;
    if (s === 'MYKAD' || s.startsWith('MYKAD ')) return true;
    if (['WARGANEGARA', 'LELAKI', 'PEREMPUAN'].includes(s)) return true;
    if (religions.has(s) || genders.has(s)) return true;
    return false;
  };

  /** Strip religion/gender/header tokens that OCR glued onto a name line. */
  const cleanNameTokens = (line: string) =>
    line
      .split(/\s+/)
      .filter(token => {
        if (!token) return false;
        if (headerNoise.has(token)) return false;
        if (religions.has(token) || genders.has(token)) return false;
        if (token.includes('PENGENAL') || token === 'MYKAD') return false;
        if (token === 'KAD' || token === 'IDENTITY' || token === 'CARD') return false;
        return true;
      })
      .join(' ')
      .trim();

  const nricRegex = /\b\d{6}-\d{2}-\d{4}\b/;
  const nric =
    uniqueLines.find(l => nricRegex.test(l))?.match(nricRegex)?.[0] || undefined;
  const nric2Regex = /\b\d{6}-\d{2}-\d{3}\b/;
  const nric2 =
    uniqueLines.find(l => nric2Regex.test(l))?.match(nric2Regex)?.[0] || undefined;

  const country = uniqueLines.find(l => l === 'MALAYSIA') || undefined;
  const nationality =
    uniqueLines.find(l => l.includes('WARGANEGARA')) || undefined;

  const malaysiaStates = new Set([
    'JOHOR',
    'KEDAH',
    'KELANTAN',
    'MELAKA',
    'NEGERI SEMBILAN',
    'PAHANG',
    'PERAK',
    'PERLIS',
    'PULAU PINANG',
    'SABAH',
    'SARAWAK',
    'SELANGOR',
    'TERENGGANU',
    'WILAYAH PERSEKUTUAN',
    'KUALA LUMPUR',
    'LABUAN',
    'PUTRAJAYA',
  ]);
  const state = uniqueLines.find(l => malaysiaStates.has(l)) || undefined;

  const streetKeywords = [
    'JALAN',
    'JLN',
    'TAMAN',
    'LORONG',
    'LRG',
    'PERSIARAN',
    'BANDAR',
    'DESA',
    'MEDAN',
    'KAMPUNG',
    'KG',
    'KAWASAN',
    'KAW',
    'KOMPLEKS',
    'PUSAT',
    'BLOK',
    'BANGUNAN',
    'PANGSAPURI',
    'APARTMENT',
    'FLAT',
    'FASA',
    'TINGKAT',
    'LANTAI',
    'RUMAH',
    'DR', // Desa / Daerah house prefix on MyKad (e.g. DR 37)
  ];
  // Address prefixes kept even though short (PT 151, DR 37, LOT 5, …)
  const addressKeepShort = /^(AL|AP|BIN|AN|EN|IN|UN|DA|DE|NO|KG|PT|LOT|DR|JLN|LRG)$/i;
  // MyKad background microtext fragments (KERAJAAN MALAYSIA / etc.)
  const noiseTokenRe =
    /KERAJA|RAJAAN|JAKERA|KEKAJ|^KERA$|MALAYS|YSIA|AYSI|ANMALAY|ANMAL|JAANM|ERAJAAN|PENGENAL|NMALA|AAKER|SIAKE|SIAK|^MYKAD$|^ANAL$|^STARE$/i;

  let postcode: string | undefined;
  let city: string | undefined;
  for (const l of uniqueLines) {
    const m = l.match(/^(\d{5})\s+(.+)$/);
    if (m) {
      postcode = m[1];
      // MyKad city is usually 1 word (PEKAN) or 2 (KUALA LUMPUR) — drop OCR junk
      const cityTokens = m[2]
        .split(/\s+/)
        .map(t => t.replace(/[^A-Z]/gi, ''))
        .filter(Boolean)
        .filter(t => !headerNoise.has(t))
        .filter(t => !noiseTokenRe.test(t))
        .filter(t => t.length >= 3)
        .filter(t => !malaysiaStates.has(t) && t !== 'MALAYSIA' && t !== 'WARGANEGARA');
      if (cityTokens.length) {
        if (
          cityTokens.length >= 2 &&
          /^(KUALA|JOHOR|KOTA|BANDAR|BUKIT|TELUK|PADANG|TANJUNG|TEMERLOH)$/i.test(cityTokens[0])
        ) {
          city = `${cityTokens[0]} ${cityTokens[1]}`;
        } else {
          // Prefer first clean token; fix common OCR stretch (PEKANER → PEKAN)
          let c = cityTokens[0];
          if (/^PEKAN/i.test(c)) c = 'PEKAN';
          if (/^KETEREH/i.test(c)) c = 'KETEREH';
          city = c;
        }
      }
      break;
    }
  }

  const isNoiseToken = (tok: string) => {
    if (!tok) return true;
    // Keep house / unit numbers (e.g. "3" in FASA 3, "27-01-22")
    if (/^\d+[A-Z]?$/i.test(tok)) return false;
    if (/^\d+(-\d+)+[A-Z]?$/i.test(tok)) return false;
    if (headerNoise.has(tok)) return true;
    if (religions.has(tok) || genders.has(tok)) return true;
    // Single-letter OCR junk from background (e.g. "C")
    if (tok.length === 1) return true;
    // Tiny fragments unless name/address particles
    if (tok.length === 2 && !addressKeepShort.test(tok)) {
      return true;
    }
    return noiseTokenRe.test(tok);
  };

  /** Only flag lines that are mostly microtext — not real name lines with 1 noise word. */
  const isBackgroundNoise = (s: string) => {
    if (!s) return true;
    if (headerNoise.has(s)) return true;
    const tokens = s.split(/\s+/).filter(Boolean);
    if (!tokens.length) return true;
    const noiseCount = tokens.filter(isNoiseToken).length;
    if (tokens.length <= 4 && noiseCount === tokens.length) return true;
    if (noiseCount / tokens.length >= 0.6) return true;
    return false;
  };

  /** Strip security-pattern tokens; keep real words (incl. A'ZIM). */
  const stripNoiseFromLine = (line: string, { keepDigits = false } = {}) => {
    let out = line
      .split(/\s+/)
      .filter(tok => !isNoiseToken(tok))
      .join(' ');
    if (!keepDigits) {
      out = out.replace(/[0-9]/g, ' ');
    }
    return out.replace(/\s+/g, ' ').trim();
  };

  const isAddressLine = (l: string) => {
    // Keep digits for house numbers like 27-01-22 / FASA 3
    const cleaned = stripNoiseFromLine(l, { keepDigits: true });
    if (!cleaned || isHeaderLike(cleaned)) return false;
    if (nric && (l.includes(nric) || cleaned.includes(nric))) return false;
    if (nric2 && (l.includes(nric2) || cleaned.includes(nric2))) return false;
    if (cleaned === country || cleaned === nationality || malaysiaStates.has(cleaned)) return false;
    if (/^\d{5}\b/.test(cleaned)) return false;
    if (/^W\.?\s*PERSEKUTUAN/i.test(cleaned)) return false;
    return (
      /\d/.test(cleaned) ||
      /FASA\s*\d+/i.test(cleaned) ||
      streetKeywords.some(k => new RegExp(`\\b${k}\\b`).test(cleaned))
    );
  };

  const isNameCandidate = (l: string, allowSingleWord = false) => {
    const cleaned = stripNoiseFromLine(cleanNameTokens(l));
    if (!cleaned || isHeaderLike(cleaned)) return false;
    if (nric && cleaned.includes(nric)) return false;
    if (cleaned === country || cleaned === nationality || malaysiaStates.has(cleaned)) return false;
    if (/^W\.?\s*PERSEKUTUAN/i.test(cleaned)) return false;
    if (streetKeywords.some(k => new RegExp(`\\b${k}\\b`).test(cleaned))) return false;
    const words = cleaned.split(/\s+/).filter(Boolean);
    if (words.length === 0) return false;
    return allowSingleWord ? words.length >= 1 : words.length >= 2;
  };

  const orderedLines = lines.map(norm).filter(Boolean);
  // Vision sometimes glues patronymic + father into one token (BINZAKARIA)
  const cleanedLines = orderedLines.map(l =>
    stripNoiseFromLine(
      cleanNameTokens(l.replace(/\b(BIN|BINTI)([A-Z]{3,})\b/gi, '$1 $2'))
    )
  );
  const nricLineIndex = orderedLines.findIndex(
    l => (nric && l.includes(nric)) || (nric2 && l.includes(nric2))
  );

  /**
   * Name extraction for Malay IC:
   * Prefer contiguous "GIVEN…" + "BIN/BINTI FATHER" lines; sanitize rebuilds order.
   */
  const extractName = (): string | undefined => {
    // Prefer a patronymic line that already includes the father name
    let binIdx = cleanedLines.findIndex(
      (l, idx) =>
        /^(BIN|BINTI)\s+[A-Z'’]{2,}/.test(l) && !isAddressLine(orderedLines[idx])
    );
    if (binIdx < 0) {
      binIdx = cleanedLines.findIndex(
        (l, idx) =>
          /\b(BIN|BINTI|A\/L|A\/P)\b/.test(l) && !isAddressLine(orderedLines[idx])
      );
    }

    if (binIdx >= 0) {
      let binCleaned = cleanedLines[binIdx]
        .replace(/[^A-Z'’\s/-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      // Vision often splits father across lines:
      //   "BIN" + "ZAKARIA"
      //   "NURZAIM … BIN" + "ZAKARIA"
      //   "ARIKHA BINTI ABDUL" + "HAMID"
      const appendFatherFromLine = (idx: number) => {
        if (idx < 0 || idx >= cleanedLines.length) return;
        const next = cleanedLines[idx];
        if (!next || isAddressLine(orderedLines[idx]) || isBackgroundNoise(next)) return;
        if (/\b(BIN|BINTI)\b/.test(next) || /\d/.test(next)) return;
        if (religions.has(next) || genders.has(next) || next.includes('WARGANEGARA')) return;
        if (streetKeywords.some(k => new RegExp(`\\b${k}\\b`).test(next))) return;
        const nextWords = next
          .replace(/[^A-Z'’\s-]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
          .split(/\s+/)
          .filter(t => t && !isNoiseToken(t));
        if (nextWords.length >= 1 && nextWords.length <= 3) {
          const existing = new Set(binCleaned.split(/\s+/));
          const add = nextWords.filter(w => !existing.has(w));
          if (add.length) {
            binCleaned = `${binCleaned} ${add.join(' ')}`.replace(/\s+/g, ' ').trim();
          }
        }
      };

      const fatherWordCount = () => {
        const toks = binCleaned.split(/\s+/).filter(Boolean);
        const p = toks.findIndex(t => /^(BIN|BINTI)$/i.test(t));
        return p >= 0 ? Math.max(0, toks.length - p - 1) : 0;
      };

      // Case A: line ends at BIN/BINTI — need father from nearby lines
      if (/^(BIN|BINTI)$/i.test(binCleaned) || /\b(BIN|BINTI)$/i.test(binCleaned)) {
        appendFatherFromLine(binIdx + 1);
        if (/\b(BIN|BINTI)$/i.test(binCleaned)) {
          for (let j = binIdx + 1; j <= Math.min(cleanedLines.length - 1, binIdx + 4); j++) {
            if (isAddressLine(orderedLines[j])) continue;
            appendFatherFromLine(j);
            if (!/\b(BIN|BINTI)$/i.test(binCleaned)) break;
          }
        }
        if (/\b(BIN|BINTI)$/i.test(binCleaned)) {
          for (let j = binIdx - 1; j >= Math.max(0, binIdx - 4); j--) {
            const cand = cleanedLines[j];
            if (!cand || isAddressLine(orderedLines[j])) continue;
            const words = cand.split(/\s+/).filter(t => t && !isNoiseToken(t));
            if (
              words.length === 1 &&
              words[0].length >= 5 &&
              !/^(NURZAIM|MUHAMMAD|MOHAMAD|SITI)$/i.test(words[0])
            ) {
              binCleaned = `${binCleaned} ${words[0]}`;
              break;
            }
          }
        }
      }

      // Case B: father started on same line but continues on next
      // e.g. "ARIKHA BINTI ABDUL" + "HAMID" + address
      // Skip microtext gaps; stop at first real address line.
      if (/\b(BIN|BINTI)\b/i.test(binCleaned) && fatherWordCount() < 2) {
        for (let j = binIdx + 1; j <= Math.min(cleanedLines.length - 1, binIdx + 5); j++) {
          const raw = orderedLines[j];
          if (isAddressLine(raw) || /^\d{5}\b/.test(raw)) break;
          const cand = cleanedLines[j];
          if (!cand || isBackgroundNoise(cand) || isHeaderLike(cand)) continue;
          if (religions.has(cand) || genders.has(cand) || cand.includes('WARGANEGARA')) continue;
          if (streetKeywords.some(k => new RegExp(`\\b${k}\\b`).test(cand))) break;
          const words = cand.split(/\s+/).filter(t => t && !isNoiseToken(t));
          // Single leftover father token (HAMID / RAZAK / ZAKARIA)
          if (words.length === 1 && words[0].length >= 4) {
            appendFatherFromLine(j);
            if (fatherWordCount() >= 2) break;
            continue;
          }
          // Multi-word non-address that isn't pure noise — stop (likely unrelated)
          if (words.length > 1) break;
        }
      }

      const givenParts: string[] = [];
      const pushGiven = (idx: number) => {
        if (idx < 0 || idx >= cleanedLines.length || idx === binIdx) return;
        let cleaned = cleanedLines[idx];
        if (!cleaned || isBackgroundNoise(cleaned)) return;
        if (isAddressLine(orderedLines[idx])) return;
        if (religions.has(cleaned) || genders.has(cleaned) || cleaned.includes('WARGANEGARA'))
          return;
        if (/^W\.?\s*PERSEKUTUAN/i.test(cleaned)) return;
        if (/\b(BIN|BINTI)\b/.test(cleaned)) return;
        if (nric && cleaned.includes(nric)) return;
        // Don't treat the father-only line we already appended as a given name
        if (
          binCleaned.includes(' ') &&
          new RegExp(
            `\\b(BIN|BINTI)\\s+.*\\b${cleaned.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`
          ).test(binCleaned)
        ) {
          return;
        }
        cleaned = cleaned.replace(/[^A-Z'’\s-]/g, ' ').replace(/\s+/g, ' ').trim();
        cleaned = cleaned
          .split(/\s+/)
          .filter(t => !isNoiseToken(t))
          .join(' ')
          .trim();
        const words = cleaned.split(/\s+/).filter(Boolean);
        if (!words.length) return;
        if (words.length >= 2 || (words.length === 1 && words[0].length >= 5)) {
          if (!givenParts.includes(cleaned)) givenParts.push(cleaned);
        }
      };

      // Contiguous lines immediately before BIN (skip a father-only one-word line)
      for (let i = binIdx - 1; i >= Math.max(0, binIdx - 4); i--) {
        const cand = cleanedLines[i];
        const words = (cand || '').split(/\s+/).filter(Boolean);
        // Skip lone father word already merged after BIN
        if (
          words.length === 1 &&
          binCleaned.toUpperCase().endsWith(` ${words[0]}`)
        ) {
          continue;
        }
        const before = givenParts.length;
        pushGiven(i);
        if (givenParts.length === before && words.length !== 1) break;
        if (givenParts.length > before) {
          // keep scanning for more given lines; stop after first gap of non-name
        } else if (words.length === 1) {
          continue; // skipped father-only / noise, keep looking
        } else {
          break;
        }
      }
      givenParts.reverse();

      if (!givenParts.length) {
        const from = nricLineIndex >= 0 ? nricLineIndex + 1 : Math.max(0, binIdx - 3);
        for (let i = from; i < binIdx; i++) pushGiven(i);
      }

      // If BIN line already contains given+BIN(+father), don't duplicate given
      const binHasGiven =
        !/^(BIN|BINTI)\b/i.test(binCleaned) && /\b(BIN|BINTI)\b/i.test(binCleaned);
      const parts = binHasGiven
        ? [binCleaned]
        : [...givenParts, binCleaned].filter(Boolean);
      if (parts.length) return parts.join(' ');
    }

    // Regex: "GIVEN NAME BIN FATHER NAME" — pick best (prefer NUR/MUHAMMAD… starters)
    const joined = cleanedLines.join(' ');
    const nameRe =
      /([A-Z][A-Z'’]*(?:\s+[A-Z][A-Z'’]*){0,8})\s+(BIN|BINTI)\s+([A-Z][A-Z'’]*(?:\s+[A-Z][A-Z'’]*){0,6})/g;
    let best: RegExpMatchArray | null = null;
    let bestScore = -1;
    let m: RegExpMatchArray | null;
    while ((m = nameRe.exec(joined)) !== null) {
      const before = m[1].trim();
      if (streetKeywords.some(k => before.includes(k)) || /\d/.test(before)) continue;
      if (isBackgroundNoise(before)) continue;
      if (/YSIA|JAKERA|KEKAJ|KERAJA|RAJAAN|MALAYS|PENGENAL/i.test(before)) continue;
      let score = before.split(/\s+/).length;
      if (/^(MUHAMMAD|MOHAMAD|MOHD|AHMAD|SITI|NUR|NURUL|WAN|SYED)/i.test(before)) score += 5;
      if (score > bestScore) {
        bestScore = score;
        best = m;
      }
    }
    if (best) {
      return `${best[1]} ${best[2]} ${best[3]}`.replace(/\s+/g, ' ').trim();
    }

    const nameParts: string[] = [];
    const startIdx = nricLineIndex >= 0 ? nricLineIndex + 1 : 0;
    for (let i = startIdx; i < orderedLines.length; i++) {
      const cleaned = cleanedLines[i];
      if (!cleaned) continue;
      if (isAddressLine(orderedLines[i]) || malaysiaStates.has(cleaned) || /^\d{5}\b/.test(cleaned)) {
        break;
      }
      if (
        cleaned.includes('WARGANEGARA') ||
        religions.has(cleaned) ||
        genders.has(cleaned) ||
        /^W\.?\s*PERSEKUTUAN/i.test(cleaned)
      ) {
        if (nameParts.length > 0) break;
        continue;
      }
      if (isHeaderLike(cleaned) || isBackgroundNoise(cleaned)) {
        if (nameParts.length > 0) break;
        continue;
      }
      if (isNameCandidate(orderedLines[i], nameParts.length > 0)) {
        nameParts.push(cleaned);
        if (nameParts.length >= 4) break;
      } else if (nameParts.length > 0) {
        break;
      }
    }
    return nameParts.length ? nameParts.join(' ') : undefined;
  };

  const name = extractName();
  console.log('MyKad OCR lines:', orderedLines);
  console.log('MyKad cleaned lines:', cleanedLines);
  console.log('MyKad extracted name:', name);

  // Address: from first house/street line after name → until postcode line
  const nameUpper = (name || '').toUpperCase();
  const startIdx = nricLineIndex >= 0 ? nricLineIndex + 1 : 0;
  const postcodeLineIndex = orderedLines.findIndex(l => /^\d{5}\b/.test(l));

  let addressStart = -1;
  for (let i = startIdx; i < orderedLines.length; i++) {
    if (postcodeLineIndex >= 0 && i >= postcodeLineIndex) break;
    const line = orderedLines[i];
    if (/\b(BIN|BINTI)\b/.test(line) && !/\d/.test(line)) continue;
    if (nameUpper && stripNoiseFromLine(line, { keepDigits: true }) === nameUpper) continue;
    if (isAddressLine(line)) {
      addressStart = i;
      break;
    }
  }

  const cleanAddressLine = (line: string) =>
    stripNoiseFromLine(line, { keepDigits: true })
      .split(/\s+/)
      .filter(token => {
        if (!token) return false;
        if (headerNoise.has(token)) return false;
        if (/MALAYS|YSIA|KERAJA|RAJAAN|JAKERA|KEKAJ|^KERA$|ANMAL|NMALA|JAANM|AAKER|SIAKE|^AKE$|^ANAL$|^STARE$/i.test(token))
          return false;
        // Drop tiny OCR crumbs — keep PT/DR/KG/NO and real words
        if (
          token.length <= 3 &&
          !/^(PT|DR|KG|NO|LOT|JLN|LRG)$/i.test(token) &&
          !/^\d/.test(token)
        ) {
          return false;
        }
        return true;
      })
      .join(' ')
      .trim();

  const isAddressContinuation = (l: string) => {
    const cleaned = cleanAddressLine(l);
    if (!cleaned) return false;
    if (isHeaderLike(cleaned) || isBackgroundNoise(cleaned)) return false;
    if (malaysiaStates.has(cleaned) || /^W\.?\s*PERSEKUTUAN/i.test(cleaned)) return false;
    if (religions.has(cleaned) || genders.has(cleaned) || cleaned.includes('WARGANEGARA')) return false;
    if (/\b(BIN|BINTI)\b/.test(cleaned) && !/\d/.test(cleaned)) return false;
    if (nric && cleaned.includes(nric)) return false;
    // Skip leftover father-name line if still sitting above the house/street block
    if (
      nameUpper &&
      (nameUpper.includes(cleaned) ||
        nameUpper.split(/\s+/).includes(cleaned) ||
        cleaned.split(/\s+/).every(w => nameUpper.split(/\s+/).includes(w)))
    ) {
      return false;
    }
    // Always keep phase / block / floor lines (e.g. FASA 3)
    if (/\b(FASA|PHASE|BLOK|BLOCK|TINGKAT|LANTAI|UNIT)\b/i.test(cleaned)) return true;
    if (isAddressLine(l)) return true;
    const words = cleaned.split(/\s+/).filter(Boolean);
    // Reject lone short junk tokens (OCR crumbs)
    if (words.length === 1 && words[0].length <= 3 && !/^\d/.test(words[0])) return false;
    // Keep locality lines (MENGGATAL, KUALA PAHANG, …) between street and postcode
    if (words.length >= 1 && words.length <= 6 && !/\d{6}-\d{2}-\d{4}/.test(cleaned)) {
      if (words.every(w => /^[A-Z'’.()-]+$/i.test(w) || /^\d+[A-Z-]*$/i.test(w) || /^\d+(-\d+)+$/i.test(w))) {
        return true;
      }
    }
    return false;
  };

  const addressParts: string[] = [];
  if (addressStart >= 0) {
    const end = postcodeLineIndex >= 0 ? postcodeLineIndex : orderedLines.length;
    for (let i = addressStart; i < end; i++) {
      const line = orderedLines[i];
      // Also skip pure name leftovers (HAMID) that appear before house no.
      const cleanedProbe = cleanAddressLine(line);
      if (
        cleanedProbe &&
        nameUpper &&
        !/\d/.test(cleanedProbe) &&
        !streetKeywords.some(k => new RegExp(`\\b${k}\\b`).test(cleanedProbe)) &&
        nameUpper.split(/\s+/).includes(cleanedProbe)
      ) {
        continue;
      }
      if (!isAddressContinuation(line)) continue;
      const cleaned = cleanAddressLine(line);
      if (cleaned && !addressParts.includes(cleaned)) addressParts.push(cleaned);
    }
  }

  // If locality line missing, pull any non-noise alpha line between last street line and postcode
  if (addressParts.length > 0 && postcodeLineIndex > addressStart) {
    const lastStreetIdx = (() => {
      for (let i = postcodeLineIndex - 1; i >= addressStart; i--) {
        if (isAddressLine(orderedLines[i])) return i;
      }
      return addressStart;
    })();
    for (let i = lastStreetIdx + 1; i < postcodeLineIndex; i++) {
      const cleaned = cleanAddressLine(orderedLines[i]);
      if (!cleaned || addressParts.includes(cleaned)) continue;
      if (isHeaderLike(cleaned) || isBackgroundNoise(cleaned)) continue;
      if (malaysiaStates.has(cleaned)) continue;
      if (nameUpper && nameUpper.split(/\s+/).includes(cleaned)) continue;
      const words = cleaned.split(/\s+/).filter(Boolean);
      if (
        words.length >= 1 &&
        words.length <= 4 &&
        words.every(w => /^[A-Z][A-Z'-]*$/i.test(w) && w.length >= 4)
      ) {
        addressParts.push(cleaned);
      }
    }
  }

  // Fallback: keyword/digit address lines only
  const addressLines =
    addressParts.length > 0
      ? addressParts
      : uniqueLines.filter(isAddressLine).map(cleanAddressLine).filter(Boolean);

  const filteredAddressLines = addressLines.filter(
    l =>
      l &&
      !(postcode && l.startsWith(postcode)) &&
      l !== city &&
      l !== state &&
      !isBackgroundNoise(l) &&
      !/MALAYS|KERAJA|RAJAAN|ANMAL|NMALA|^KERA$|^STARE$/i.test(l)
  );

  const address = filteredAddressLines.length
    ? filteredAddressLines.join(', ')
    : undefined;

  // Prefer explicit WP / KL lines for state when present
  const wpStateLine = orderedLines.find(l => /^W\.?\s*PERSEKUTUAN/i.test(l));
  const resolvedState =
    wpStateLine ||
    (city && /KUALA LUMPUR/i.test(city) ? 'W. PERSEKUTUAN(KL)' : undefined) ||
    state;

  console.log('MyKad extracted address:', address, { postcode, city, state: resolvedState });

  return {
    name,
    nationality,
    nric,
    address,
    country,
    state: resolvedState,
    city,
    postcode,
  };
};

/** Final cleanup for MyKad FullName after OCR (header / religion / gender leftovers). */
const sanitizeMyKadName = (raw?: string) => {
  if (!raw) return '';
  const dropExact = new Set([
    'KAD',
    'PENGENALAN',
    'IDENTITY',
    'CARD',
    'MYKAD',
    'MALAYSIA',
    'ISLAM',
    'BUDDHA',
    'BUDDHIST',
    'CHRISTIAN',
    'HINDU',
    'SIKH',
    'TAOIST',
    'CONFUCIAN',
    'LELAKI',
    'PEREMPUAN',
    'MALE',
    'FEMALE',
    'WARGANEGARA',
  ]);

  const nameStartRe =
    /^(MUHAMMAD|MOHAMAD|MOHAMMED|MOHD|AHMAD|ABDULLAH|ABDUL|ABU|SITI|NUR|NURUL|WAN|SYED|SHARIFAH|PUTERI|TENGKU|DATO|DATUK)$/i;

  const microtextRe =
    /MALAY|YSIA|AYSI|KERAJA|RAJAAN|JAKERA|KEKAJ|^KERA$|ANMAL|NMALA|JAANM|AAKER|SIAKE|SIAK|ERAJAAN|PENGENAL|^STARE$/i;

  const isPatronymic = (t: string) => /^(BIN|BINTI|A\/L|A\/P)$/i.test(t);

  const isPlausibleNameToken = (t: string) => {
    if (!t || isPatronymic(t)) return false;
    if (dropExact.has(t.toUpperCase())) return false;
    if (microtextRe.test(t)) return false;
    return /^[A-Z][A-Z'’-]*$/i.test(t) && t.replace(/['’-]/g, '').length >= 2;
  };

  let tokens = raw
    .replace(/[''`′´]/g, "'")
    .replace(/\bMyKad(?:\s*C)?\b/gi, ' ')
    .replace(/\bKAD\s*PENGENAL\w*\b/gi, ' ')
    .replace(/\bIDENTITY\s*CARD\b/gi, ' ')
    // Vision sometimes glues patronymic + father: BINZAKARIA / BINTIOSMAN
    .replace(/\b(BIN|BINTI)([A-Z]{3,})\b/gi, '$1 $2')
    .split(/\s+/)
    .map(t => t.trim())
    .filter(Boolean)
    .filter(token => {
      const t = token.toUpperCase();
      if (dropExact.has(t)) return false;
      if (t.includes('PENGENAL')) return false;
      if (microtextRe.test(t)) return false;
      if (t.length === 1) return false;
      if (t.length === 2 && !/^(AL|AP|AN|EN|IN|UN|DA|DE|BIN)$/i.test(t)) return false;
      return true;
    });

  // Rebuild around BIN/BINTI so father name is always after the patronymic
  const binI = tokens.findIndex(t => /^(BIN|BINTI)$/i.test(t));
  if (binI >= 0) {
    let father = tokens.slice(binI + 1).filter(isPlausibleNameToken);
    let before = tokens.slice(0, binI).filter(t => isPlausibleNameToken(t) || nameStartRe.test(t));

    // OCR order bug: "ZAKARIA NURZAIM FARHANIEZAM BIN" (father before given, BIN at end)
    if (!father.length && before.length >= 3) {
      const startGiven = before.findIndex(
        t => nameStartRe.test(t) || /^NUR[A-Z]/i.test(t) || /^MUHAM/i.test(t)
      );
      if (startGiven > 0) {
        father = before.slice(0, startGiven).slice(-2);
        before = before.slice(startGiven);
      } else {
        // Assume last 2 words are given name; leftovers before = father
        const givenCount = before.length >= 4 ? 3 : 2;
        father = before.slice(0, before.length - givenCount).slice(-2);
        before = before.slice(before.length - givenCount);
      }
    } else if (before.length) {
      // Normal: keep only the trailing given-name run before BIN (drop leading junk)
      const startGiven = before.findIndex(
        t => nameStartRe.test(t) || /^NUR[A-Z]/i.test(t) || /^MUHAM/i.test(t)
      );
      if (startGiven >= 0) {
        before = before.slice(startGiven);
      } else {
        // Keep up to 4 tokens immediately before BIN
        before = before.slice(-4);
      }
    }

    tokens = [...before, tokens[binI], ...father];
  } else {
    // No BIN — drop leading garbage until first real name token
    const startIdx = tokens.findIndex(t => nameStartRe.test(t) || isPlausibleNameToken(t));
    if (startIdx > 0) tokens = tokens.slice(startIdx);
  }

  return tokens.join(' ').trim();
};

const OCRProcessing = async (tempResponse: any) => {
  const scanData = tempResponse?.responses?.[0]?.fullTextAnnotation?.text?.split('\n');
  if (!scanData?.length) {
    return {
      isMyKad: false,
      isPassport: false,
      mykadno: '',
      mykadname: '',
      mykadaddress: '',
      mykadpostcode: '',
      mykadcity: '',
      mykadstate: '',
      passportnationality: '',
      passportcountry: '',
      passportholdername: '',
      passportnumber: '',
      passportbirthdate: '',
    };
  }

  const parseMykad = parseMyKad(scanData);

  let scanDataArr: string[] = [];
  for (let i = 0; i < scanData.length; i++) {
    if (scanData[i].length >= 5) {
      scanDataArr.push(scanData[i]);
    }
  }
  scanDataArr = _.without(scanDataArr, 'MyKad');
  scanDataArr = _.without(scanDataArr, 'Mykad');
  scanDataArr = _.without(scanDataArr, 'MALAYSIA');
  scanDataArr = _.without(scanDataArr, 'IDENTITY CARD');
  scanDataArr = _.without(scanDataArr, '');

  const regex_myr_mykad = /mykad/gim;
  const regex_myr_mytentera = /Tentera/gim;
  const regex_myr_identity = /\d{6}-\d{2}-\d{4}/gim;
  const regex_passport = /passport|<{1,}/gim;

  const isMyKad =
    scanData.findIndex((str: string) => regex_myr_mykad.test(str)) > 0 ||
    scanData.findIndex((str: string) => regex_myr_identity.test(str)) > 0;
  const isPassport = scanData.findIndex((str: string) => regex_passport.test(str)) > 0;
  const isMilitaryCard =
    scanData.findIndex((str: string) => regex_myr_mytentera.test(str)) > 0;

  let mykadno = '';
  let mykadname = '';
  let mykadaddress = '';
  let mykadpostcode = '';
  let mykadcity = '';
  let mykadstate = '';
  let passportnationality = '';
  let passportcountry = '';
  let passportholdername = '';
  let passportnumber = '';
  let passportbirthdate = '';
  let passportgender = '';
  let passportexpiredate: any = '';
  let passportidentityno = '';
  let passportcountrycode = '';

  if (isMyKad) {
    const index_identity = scanData.findIndex((str: string) =>
      regex_myr_identity.test(str)
    );
    mykadno = scanData[index_identity];

    const regex_address_postcode = /\b\d{5}\b/g;
    const index_postcode = scanData.findIndex((str: string) =>
      regex_address_postcode.test(str)
    );

    let index_start_address = index_postcode - 1;
    if (/\d+/.test(scanData[index_start_address - 2])) {
      index_start_address -= 2;
    }
    if (/\d+/.test(scanData[index_start_address - 1])) {
      index_start_address -= 1;
    }
    if (isMilitaryCard) {
      index_start_address -= 2;
    }

    mykadaddress =
      parseMykad?.address ||
      scanData.slice(index_start_address, index_postcode).join(' ');
    mykadpostcode = scanData[index_postcode]?.substr(0, 5) || parseMykad?.postcode || '';
    mykadcity = parseMykad?.city || scanData[index_postcode]?.substr(6) || '';
    mykadstate = parseMykad?.state || scanData[index_postcode + 1]?.trim() || '';

    // Clean fallback city (e.g. "PEKANER ANAL" → "PEKAN")
    if (mykadcity && !parseMykad?.city) {
      const toks = mykadcity
        .toUpperCase()
        .split(/\s+/)
        .map(t => t.replace(/[^A-Z]/g, ''))
        .filter(t => t.length >= 3 && !/ANAL|KERAJA|MALAYS|YSIA/i.test(t));
      if (toks[0]) {
        let c = toks[0];
        if (/^PEKAN/i.test(c)) c = 'PEKAN';
        if (/^KETEREH/i.test(c)) c = 'KETEREH';
        mykadcity = c;
      }
    }

    // Prefer parser name/address (multi-line name + cleaned address)
    mykadname = sanitizeMyKadName(
      parseMykad?.name ||
        (isMilitaryCard
          ? scanData.slice(index_identity + 2, index_start_address).join(' ')
          : scanData.slice(index_identity + 1, index_start_address).join(' '))
    );

    // Last-resort / normalize: ends with BIN (no father) or starts at patronymic
    const startsAtPatron =
      /^(BIN|BINTI|A\/L|A\/P)\b/i.test(mykadname || '');
    let endsWithPatronNoFather =
      /\b(BIN|BINTI)$/i.test((mykadname || '').trim());
    const fatherOnly =
      !!mykadname &&
      !/\b(BIN|BINTI|A\/L|A\/P)\b/i.test(mykadname) &&
      new RegExp(
        `\\b(BIN|BINTI)\\s+${mykadname.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`,
        'i'
      ).test((scanData || []).join(' '));

    if (startsAtPatron || endsWithPatronNoFather || fatherOnly) {
      const rawJoined = (scanData || [])
        .join(' ')
        .toUpperCase()
        .replace(/[''`′´]/g, "'")
        // Unglue BINZAKARIA → BIN ZAKARIA
        .replace(/\b(BIN|BINTI)([A-Z]{3,})\b/g, '$1 $2')
        .replace(/\s+/g, ' ');
      const nameRe =
        /([A-Z][A-Z']{1,}(?:\s+[A-Z][A-Z']{1,}){0,8})\s+(BIN|BINTI)\s+([A-Z][A-Z']*(?:\s+[A-Z][A-Z']*){0,5})/g;
      let best: RegExpMatchArray | null = null;
      let bestScore = -1;
      let m: RegExpMatchArray | null;
      while ((m = nameRe.exec(rawJoined)) !== null) {
        const before = m[1].trim();
        const after = (m[3] || '').trim();
        if (/JALAN|TAMAN|LORONG|PANGSAPURI|BANDAR|FASA|WARGANEGARA|\d{5}|\d{6}-/.test(before)) {
          continue;
        }
        if (/KERAJA|RAJAAN|JAKERA|YSIA|KEKAJ|MALAYS|PENGENAL|IDENTITY|MYKAD/i.test(before.replace(/\s/g, ''))) {
          continue;
        }
        // Father must not look like address
        if (/JALAN|TAMAN|PANGSAPURI|BANDAR|FASA|KAMPUNG|\d/.test(after)) continue;
        let score = before.split(/\s+/).length + after.split(/\s+/).filter(Boolean).length;
        if (/^(MUHAMMAD|MOHAMAD|MOHD|AHMAD|SITI|NUR)/i.test(before)) score += 5;
        if (score > bestScore) {
          bestScore = score;
          best = m;
        }
      }
      if (best) {
        mykadname = sanitizeMyKadName(`${best[1]} ${best[2]} ${best[3]}`);
      } else if (endsWithPatronNoFather) {
        // Line-walk: find father on the line after a line ending with BIN/BINTI
        const lines = (scanData || []).map((s: string) =>
          String(s)
            .toUpperCase()
            .replace(/[''`′´]/g, "'")
            .replace(/\b(BIN|BINTI)([A-Z]{3,})\b/g, '$1 $2')
            .replace(/\s+/g, ' ')
            .trim()
        );
        let recovered = '';
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          if (!/\b(BIN|BINTI)\b/.test(line)) continue;
          // Same-line father
          const same = line.match(/\b(BIN|BINTI)\s+([A-Z][A-Z']{2,}(?:\s+[A-Z][A-Z']{2,}){0,2})\b/);
          if (same && !/TAMAN|JALAN|KAMPUNG|PANGSAPURI|FASA|PT\b|\d/.test(same[2])) {
            recovered = same[2];
            break;
          }
          if (!/\b(BIN|BINTI)$/.test(line)) continue;
          for (let j = i + 1; j < Math.min(i + 5, lines.length); j++) {
            const n = lines[j];
            if (!n || /\d/.test(n)) continue;
            if (/TAMAN|JALAN|KAMPUNG|PANGSAPURI|FASA|BANDAR|WARGANEGARA|ISLAM|LELAKI|PEREMPUAN|PT\b|KELANTAN|SELANGOR/.test(n))
              continue;
            const words = n.split(/\s+/).filter(w => /^[A-Z][A-Z']{2,}$/.test(w));
            if (words.length >= 1 && words.length <= 2) {
              recovered = words.join(' ');
              break;
            }
          }
          if (recovered) break;
        }
        mykadname = sanitizeMyKadName(
          recovered ? `${mykadname} ${recovered}` : mykadname
        );
      } else {
        mykadname = sanitizeMyKadName(mykadname);
      }
    }

    // Final safety: if still ends with BIN/BINTI, try glued token anywhere in OCR
    endsWithPatronNoFather = /\b(BIN|BINTI)$/i.test((mykadname || '').trim());
    if (endsWithPatronNoFather) {
      const glued = (scanData || [])
        .join(' ')
        .toUpperCase()
        .match(/\b(BIN|BINTI)([A-Z]{3,})\b/);
      if (glued && !/TAMAN|JALAN|KAMPUNG|MALAY|KERAJA|WARGANEGARA/.test(glued[2])) {
        mykadname = sanitizeMyKadName(
          mykadname.replace(/\b(BIN|BINTI)$/i, `${glued[1]} ${glued[2]}`)
        );
      }
    }

    // Father name split onto next line: "ARIKHA BINTI ABDUL" + "HAMID"
    {
      const toks = (mykadname || '').toUpperCase().split(/\s+/).filter(Boolean);
      const pIdx = toks.findIndex(t => /^(BIN|BINTI)$/.test(t));
      const fatherCount = pIdx >= 0 ? toks.length - pIdx - 1 : 99;
      if (pIdx >= 0 && fatherCount < 2) {
        const lines = (scanData || []).map((s: string) =>
          String(s).toUpperCase().replace(/\s+/g, ' ').trim()
        );
        const nameLineIdx = lines.findIndex(
          l => /\b(BIN|BINTI)\b/.test(l) && (fatherCount === 0 || l.includes(toks[pIdx + 1] || '___'))
        );
        const start = nameLineIdx >= 0 ? nameLineIdx + 1 : 0;
        for (let j = start; j < Math.min(start + 6, lines.length); j++) {
          const n = lines[j];
          if (!n) continue;
          if (/\d/.test(n) || /TAMAN|JALAN|LORONG|KAMPUNG|PANGSAPURI|FASA|BANDAR|NO\b|PT\b|DR\b/.test(n)) {
            break;
          }
          if (/WARGANEGARA|ISLAM|LELAKI|PEREMPUAN|KERAJA|MALAYS|STARE|^KERA$/.test(n)) continue;
          const words = n
            .split(/\s+/)
            .filter(w => /^[A-Z][A-Z']{3,}$/.test(w) && !/KERA|STARE|YSIA|ANAL/.test(w));
          if (words.length === 1 && !toks.includes(words[0])) {
            mykadname = sanitizeMyKadName(`${mykadname} ${words[0]}`);
            toks.push(words[0]);
            if (toks.length - pIdx - 1 >= 2) break;
            continue;
          }
          if (words.length > 1) break;
        }
      }
    }

    // Prefer parser address (do NOT fall back to raw OCR slice — it pulls name/microtext)
    if (parseMykad?.address) {
      mykadaddress = parseMykad.address;
    } else {
      // Clean the slice fallback if parser missed address
      mykadaddress = (mykadaddress || '')
        .split(/\s+/)
        .filter(
          t =>
            t &&
            !/MALAYS|KERAJA|RAJAAN|ANMAL|NMALA|JAANM|AAKER|SIAKE/i.test(t) &&
            t.length > 1
        )
        .join(' ')
        .trim();
    }
    if (parseMykad?.state) {
      mykadstate = parseMykad.state;
    }
    if (parseMykad?.city) {
      mykadcity = parseMykad.city;
    }
    if (parseMykad?.postcode) {
      mykadpostcode = parseMykad.postcode;
    }
  } else {
    let footerInfo = scanData
      .filter((str: string) => str.includes('<'))
      .map((str: string) => str.replace(/\s/g, ''));

    if (footerInfo.length !== 2) {
      footerInfo = scanData
        .map((str: string) => str.replace(/\s/g, ''))
        .filter((s: string) => /^[A-Z0-9<]+$/.test(s) && s.length >= 40);
    }
    if (footerInfo.length === 2) {
      passportcountrycode = footerInfo[0].substr(2, 3);

      const nameInfo = footerInfo[0].substr(5, 39);
      const surname = nameInfo.substring(0, nameInfo.indexOf('<'));
      const givenName = nameInfo.substring(nameInfo.indexOf('<')).replace(/</g, ' ');

      passportholdername = `${surname}${givenName}`.trim().replace(/\s+/g, ' ');
      passportnumber = footerInfo[1].substr(0, 9);
      passportbirthdate =
        moment(footerInfo[1].substr(13, 6), 'YYMMDD').format('YYMMDD') === 'Invalid date'
          ? moment(new Date(), 'YYMMDD').format('YYMMDD')
          : moment(footerInfo[1].substr(13, 6), 'YYMMDD').format('YYMMDD');
      passportgender = footerInfo[1].substr(20, 1);
      passportexpiredate = moment(footerInfo[1].substr(21, 6), 'YYMMDD').toISOString();

      const countryInfo = await getPassportCountry(passportcountrycode);

      let prevpassportcountry: string;
      switch (passportcountrycode) {
        case 'BTN':
          prevpassportcountry = 'Kingdom of Bhutan';
          break;
        case 'BOL':
          prevpassportcountry = 'Bolivia';
          break;
        case 'BIH':
          prevpassportcountry = 'Bosnia Herzegovina';
          break;
        case 'VGB':
          prevpassportcountry = 'British Virgin Islands';
          break;
        case 'BRN':
          prevpassportcountry = 'Brunei';
          break;
        case 'CPV':
          prevpassportcountry = 'Cape Verde';
          break;
        case 'CIV':
          prevpassportcountry = 'Cote d Ivoire';
          break;
        case 'DMA':
          prevpassportcountry = 'Dominica Commonwealth';
          break;
        case 'FLK':
          prevpassportcountry = 'Falkland Islands';
          break;
        case 'FRO':
          prevpassportcountry = 'Faeroe Island';
          break;
        case 'GLP':
          prevpassportcountry = 'Guadaloupe';
          break;
        case 'GNB':
          prevpassportcountry = 'Guinea Bissau';
          break;
        case 'IRN':
          prevpassportcountry = 'Iran';
          break;
        case 'PRK':
          prevpassportcountry = 'North Korea';
          break;
        case 'KOR':
          prevpassportcountry = 'South Korea';
          break;
        case 'LAO':
          prevpassportcountry = 'Laos';
          break;
        case 'MAC':
          prevpassportcountry = 'Macau';
          break;
        case 'MKD':
          prevpassportcountry = 'Macedonia';
          break;
        case 'MDA':
          prevpassportcountry = 'Moldova';
          break;
        case 'NIU':
          prevpassportcountry = 'Niue Island';
          break;
        case 'PSE':
          prevpassportcountry = 'Palestine';
          break;
        case 'REU':
          prevpassportcountry = 'Reunion';
          break;
        case 'RUS':
          prevpassportcountry = 'Russia';
          break;
        case 'SHN':
          prevpassportcountry = 'St. Helena';
          break;
        case 'KNA':
          prevpassportcountry = 'St. Kitts and Nevis';
          break;
        case 'LCA':
          prevpassportcountry = 'St_Lucia';
          break;
        case 'SPM':
          prevpassportcountry = 'St. Pierre and Miquelon';
          break;
        case 'VCT':
          prevpassportcountry = 'St. Vincent and the Grenadines';
          break;
        case 'SYR':
          prevpassportcountry = 'Syria';
          break;
        case 'TZA':
          prevpassportcountry = 'Tanzania';
          break;
        case 'GBR':
          prevpassportcountry = 'United Kingdom';
          break;
        case 'VEN':
          prevpassportcountry = 'Venezuela';
          break;
        case 'VIR':
          prevpassportcountry = 'US Virgin Islands';
          break;
        default:
          prevpassportcountry = countryInfo?.country;
      }

      let prevpassportnationality: string;
      switch (passportcountrycode) {
        case 'ARG':
          prevpassportnationality = 'Argentine';
          break;
        case 'BWA':
          prevpassportnationality = 'Batswana';
          break;
        case 'SLV':
          prevpassportnationality = 'Salvadorian';
          break;
        case 'ECU':
          prevpassportnationality = 'Ecuadorian';
          break;
        case 'ISL':
          prevpassportnationality = 'Icelandic';
          break;
        case 'LAO':
          prevpassportnationality = 'Lao';
          break;
        case 'LUX':
          prevpassportnationality = 'Luxembourgers';
          break;
        case 'MDV':
          prevpassportnationality = 'Maldivian';
          break;
        case 'NZL':
          prevpassportnationality = 'New Zealand';
          break;
        case 'PSE':
          prevpassportnationality = 'Palestinians';
          break;
        case 'PHL':
          prevpassportnationality = 'Philippine';
          break;
        case 'SAU':
          prevpassportnationality = 'Saudi';
          break;
        case 'KOR':
        case 'PRK':
          prevpassportnationality = 'Korean';
          break;
        case 'TJK':
          prevpassportnationality = 'Tajikistani';
          break;
        default:
          prevpassportnationality = countryInfo?.nationality;
      }

      passportnationality = prevpassportnationality;
      passportcountry = prevpassportcountry;
      passportexpiredate = '';
    }
  }

  return {
    isMyKad,
    isPassport,
    mykadno,
    mykadname,
    mykadaddress,
    mykadpostcode,
    mykadcity,
    mykadstate,
    passportcountrycode,
    passportnationality,
    passportcountry,
    passportholdername,
    passportnumber,
    passportbirthdate,
    passportgender,
    passportexpiredate,
    passportidentityno,
  };
};

export type OCRResult = {
  isMyKad?: boolean;
  isPassport?: boolean;
  mykadno?: string;
  mykadname?: string;
  mykadaddress?: string;
  mykadpostcode?: string;
  mykadcity?: string;
  mykadstate?: string;
  passportcountrycode?: string;
  passportnationality?: string;
  passportcountry?: string;
  passportholdername?: string;
  passportnumber?: string;
  passportbirthdate?: string;
  passportgender?: string;
  passportexpiredate?: string;
  passportidentityno?: string;
  base64: string;
  picture: string;
  Blob: Blob;
};

export const OCRhelper = async (file: File): Promise<OCRResult> => {
  // Watermark for storage; OCR runs on a separate compressed copy (no watermark)
  // so text extraction stays accurate. Both are scoped per-call for concurrency.
  const [watermarkedBlob, base64ForOCR] = await Promise.all([
    watermark(file),
    compressForOCR(file),
  ]);

  const picture = await readAsDataURL(watermarkedBlob);
  const base64 = picture.split('base64,')[1];
  const ocrResult = (await submitToGoogle(base64ForOCR)) || {};

  return { ...ocrResult, base64, picture, Blob: watermarkedBlob };
};

export function b64toBlob(b64Data: any, contentType: any, sliceSize: any) {
  contentType = contentType || '';
  sliceSize = sliceSize || 512;

  var byteCharacters = atob(b64Data);
  let byteArrays: any = [];

  for (var offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    var slice = byteCharacters.slice(offset, offset + sliceSize);
    var byteNumbers = new Array(slice.length);
    for (var i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    let byteArray: any = new Uint8Array(byteNumbers) as any;
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: contentType });
}

/** Map OCR MyKad state text to Pre Check-In State dropdown values. */
export const mapOCRState = (ocrState?: string, stateOptions: string[] = []) => {
  if (!ocrState) return '';
  const normalized = ocrState.replace(/_/g, ' ').trim().toUpperCase();

  const aliases: Record<string, string> = {
    'KUALA LUMPUR': 'Wilayah Persekutuan Kuala Lumpur',
    'W. PERSEKUTUAN(KL)': 'Wilayah Persekutuan Kuala Lumpur',
    'W.PERSEKUTUAN(KL)': 'Wilayah Persekutuan Kuala Lumpur',
    'W PERSEKUTUAN(KL)': 'Wilayah Persekutuan Kuala Lumpur',
    'W. PERSEKUTUAN (KL)': 'Wilayah Persekutuan Kuala Lumpur',
    'WILAYAH PERSEKUTUAN': 'Wilayah Persekutuan Kuala Lumpur',
    'WILAYAH PERSEKUTUAN KUALA LUMPUR': 'Wilayah Persekutuan Kuala Lumpur',
    LABUAN: 'Wilayah Persekutuan Labuan',
    PUTRAJAYA: 'Wilayah Persekutuan Putrajaya',
    PENANG: 'Pulau Pinang',
    'PULAU PINANG': 'Pulau Pinang',
    'NEGERI SEMBILAN': 'Negeri Sembilan',
  };

  if (aliases[normalized]) return aliases[normalized];

  const matched = stateOptions.find(
    (s: string) => s.replace(/_/g, ' ').toUpperCase() === normalized
  );
  if (matched) return matched;

  // Title-case fallback (matches PMS Walk-In behaviour for single-word states)
  return ocrState
    .toLowerCase()
    .split(/\s+/)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

/** Find closest enum option (spaces / underscores / case insensitive). */
export const matchEnumOption = (value?: string, options: string[] = []) => {
  if (!value) return '';
  const normalized = value.replace(/_/g, ' ').trim().toLowerCase();
  return (
    options.find(o => o.replace(/_/g, ' ').trim().toLowerCase() === normalized) ||
    value.replace(/_/g, ' ')
  );
};
